import { Box, Button, HStack, Text, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, Text as NativeText } from "react-native";

import ProgressBar from "../commons/ProgressBar";
import { commonStyles, isDesktopScreen, Scale } from "../constants";
import { formatNumberAsPercentage, formatNumberAsWholeNumber } from "../helpers/generalHelpers";
import type { MacroName, RecipeMacrosItemType } from "../types";

const styles = StyleSheet.create({
  rowContainer: {
    flex: 1,
  },
  transformStyle: {
    transform: [{ rotateZ: "-3deg" }],
  },
});

type Props = {
  macroDetail: RecipeMacrosItemType;
  showLabel?: boolean;
  verticalMode?: boolean;
  excludeDetail?: MacroName[];
  showUnit?: boolean;
  allowedToGoOver?: boolean;
  optionalPercentage?: number;
  showTarget?: boolean;
  showProgress?: boolean;
};

const MacroProgressWidget = ({
  macroDetail,
  excludeDetail: exceptItems,
  verticalMode,
  showLabel,
  showUnit = true,
  allowedToGoOver = false,
  optionalPercentage = undefined,
  showTarget = true,
  showProgress = true,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const theme = useTheme();

  const isDesktop = isDesktopScreen();

  const macroForDisplay = isDesktop
    ? t(`general.${macroDetail.macroName}`)
    : t(`general.${macroDetail.macroName}_short`);

  let progressText = "";
  if (exceptItems?.includes(macroDetail.macroName)) {
    // progressText = ` ${macroForDisplay}`;
    progressText = macroDetail.macroName === "kcal" ? "" : "g";
  } else {
    progressText = ` ${macroForDisplay}`;
    if (showTarget) {
      progressText = ` /${macroDetail.total} ${macroForDisplay}`;
    }
  }

  return (
    <Box flex={!verticalMode ? 1 : undefined} mx={`${Scale(3)}px`} nativeID={"MacroProgressWidget"}>
      {verticalMode ? (
        <VStack>
          {showLabel ? (
            <Text fontSize="sm" fontWeight="500" color="gray.400">
              {macroForDisplay}
            </Text>
          ) : null}
          <HStack>
            <Text testID={`macroDetail-${macroDetail.unit}`} fontSize="sm" fontWeight="600" color="gray.600">
              {macroDetail.progress ? formatNumberAsWholeNumber(macroDetail.progress) : 0}
            </Text>
            <Text fontSize="xs" color="gray.400">
              {progressText}
            </Text>
            {optionalPercentage ? (
              <Button ml="1" disabled bgColor="amber.200" size="8">
                <Text color="gray.600">{formatNumberAsPercentage(optionalPercentage)}</Text>
              </Button>
            ) : null}
          </HStack>
        </VStack>
      ) : (
        <HStack justifyContent="space-between">
          {showLabel ? (
            <Text fontSize="sm" fontWeight="500" color="gray.400">
              {macroForDisplay}
            </Text>
          ) : null}
          <HStack>
            <Text testID={`macroDetail-${macroDetail.unit}`} fontSize="sm" fontWeight="600" color="gray.600">
              {macroDetail.progress}
            </Text>
            <Text fontSize="xs" color="gray.400">{` /${macroDetail.total}`}</Text>
            {showUnit ? (
              <Text fontSize="xs" color="gray.400">
                {macroDetail.unit}
              </Text>
            ) : null}
          </HStack>
        </HStack>
      )}

      <Box>
        {exceptItems?.includes(macroDetail.macroName) || !showProgress ? null : (
          <ProgressBar
            value={(100 * (macroDetail.progress || 0)) / macroDetail.total}
            allowedToGoOver={allowedToGoOver}
            width={Scale(120)}
          />
        )}
      </Box>
    </Box>
  );
};

export default MacroProgressWidget;
